// src/components/modals/ServiceBookingModal.jsx
import React, { useState, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import api from "../../utils/api";
import { API_CONFIG } from '../../utils/apiConfig';

const ServiceBookingModal = ({ show, onHide, service, store }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    projectTitle: service?.product_name || '',
    projectDescription: '',
    budget: '',
    referenceImage: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const  [submissionResponse, setSubmissionResponse] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value,
      // Auto-fill project title with service name if empty
      projectTitle: name === 'projectTitle' ? value : (prev.projectTitle || service?.product_name || '')
    }));
  };

  // Handle file upload with your backend API
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset previous errors
    setUploadError('');

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      showToast('File size must be less than 5MB', 'error');
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a valid image file (JPEG, PNG, GIF, WebP)');
      showToast('Please upload a valid image file (JPEG, PNG, GIF, WebP)', 'error');
      return;
    }

    try {
      setUploadProgress(10);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      setUploadProgress(30);

      // Upload to your backend API
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('upload_preset', 'service_references');
      uploadData.append('folder', 'service_references');

      setUploadProgress(50);

      // Use your API endpoint for file upload
      const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      setUploadProgress(80);

      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'File upload failed');
      }

      const uploadResult = uploadResponse.data;
      
      setUploadProgress(100);
      
      setFormData(prev => ({ 
        ...prev, 
        referenceImage: uploadResult.fileUrl || uploadResult.url || uploadResult.secure_url
      }));

      showToast('Image uploaded successfully!', 'success');
      setUploadError('');

      // Reset progress after a delay
      setTimeout(() => setUploadProgress(0), 1000);

    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload image. Please try again.';
      setUploadError(errorMessage);
      showToast(errorMessage, 'error');
      setUploadProgress(0);
      setImagePreview(null);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, referenceImage: null }));
    setImagePreview(null);
    setUploadProgress(0);
    setUploadError('');
  };

  // Check if budget meets minimum requirement
  const isBudgetValid = useCallback(() => {
    if (!formData.budget || !service?.starting_price) return false;
    return parseFloat(formData.budget) >= parseFloat(service.starting_price);
  }, [formData.budget, service?.starting_price]);

  // Check if form is valid
  const isFormValid = () => {
    return formData.name && 
           formData.email && 
           formData.whatsapp && 
           formData.projectTitle && 
           formData.projectDescription && 
           formData.budget && 
           isBudgetValid();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      showToast('Please fill all required fields and ensure budget meets minimum requirement', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data
      const submissionData = {
        name: formData.name,
        email: formData.email,
        whatsapp: formData.whatsapp,
        projectTitle: formData.projectTitle,
        projectDescription: formData.projectDescription,
        budget: formData.budget,
        serviceId: service.id,
        serviceName: service.product_name,
        sellerId: store.company_id,
        startingPrice: service.starting_price,
        referenceImage: formData.referenceImage,
        storeName: store.company_name,
        storeUsername: store.username
      };

      // Submit to backend
      const response = await api.post(API_CONFIG.ORDERS.SERVICE_BOOKING, submissionData);

      if (response.data.success) {
        setIsSubmitted(true);
        setSubmissionResponse(response.data)
        showToast('Service request submitted successfully!', 'success');
      } else {
        throw new Error(response.data.error || 'Failed to submit service request');
      }

    } catch (error) {
      console.error('Service booking error:', error);
      showToast(error.response?.data?.error || error.message || 'Failed to submit service request', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate WhatsApp message with dashboard link
  const generateWhatsAppMessage = () => {
    const dashboardLink = `${window.location.origin}/orders`;
    
    const message = `Hello! I'm interested in your "${service?.product_name}" service.

📋 Project Details:
• Project Title: ${formData.projectTitle}
• Budget: ₦${parseFloat(formData.budget).toLocaleString()}
• Description: ${formData.projectDescription}

👤 My Contact Info:
• Name: ${formData.name}
• Email: ${formData.email}
• WhatsApp: ${formData.whatsapp}

${formData.referenceImage ? `📎 Reference Image: ${formData.referenceImage}` : ''}

Please send me the payment invoice after we finalize the details.

View full project details on your dashboard: ${dashboardLink}`;

    return encodeURIComponent(message);
  };

  // Get WhatsApp URL
  const getWhatsAppUrl = () => {
    if (!store?.company_phone) return '#';
    const phone = store.company_phone.replace(/\D/g, '');
    const message = generateWhatsAppMessage();
    return `https://wa.me/${phone}?text=${message}`;
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      whatsapp: '',
      projectTitle: service?.product_name || '',
      projectDescription: '',
      budget: '',
      referenceImage: null
    });
    setImagePreview(null);
    setUploadProgress(0);
    setUploadError('');
    setIsSubmitted(false);
  };

  // Close modal
  const handleClose = () => {
    handleReset();
    onHide();
  };

  if (!show) return null;

  // Success view after submission
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          <div className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
              
              <p className="text-gray-600 mb-4">
                Your service request has been sent to {store?.company_name || 'the seller'}. 
                They will review your project details and contact you shortly.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-yellow-800 mb-2">📋 Important Next Steps:</h3>
                <ul className="text-sm text-yellow-700 space-y-1 text-left">
                  <li>• Ensure the seller sends you a payment invoice</li>
                  <li>• Review the invoice details carefully</li>
                  <li>• Make payment only after negotiation is complete</li>
                  <li>• Keep all communication records</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Close
                </button>
                
                {store?.company_phone && (
                  <a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium text-center"
                  >
                    Contact on WhatsApp
                  </a>
                )}
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Seller can view full details in their dashboard
                </p>
                <a
                  href={`${window.location.origin}/track/${submissionResponse.orderId}?type=service`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Track My Order
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form view
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Book Service</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Service Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-purple-900 mb-2">Service Details</h3>
            <p className="text-purple-700">{service?.product_name}</p>
            {service?.starting_price > 0 && (
              <p className="text-purple-600 text-sm mt-1">
                Minimum Budget: <strong>₦{parseFloat(service.starting_price).toLocaleString()}</strong>
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Personal Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Number *
                </label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Enter your WhatsApp number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Budget (₦) *
                </label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                  min={service?.starting_price || 0}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
                    formData.budget && !isBudgetValid() 
                      ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-300' 
                      : 'border-gray-300'
                  }`}
                  placeholder={`Minimum: ₦${parseFloat(service?.starting_price || 0).toLocaleString()}`}
                />
                {formData.budget && !isBudgetValid() && (
                  <div className="flex items-center mt-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">
                      Budget must be at least ₦{parseFloat(service?.starting_price || 0).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Project Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Title *
                </label>
                <input
                  type="text"
                  name="projectTitle"
                  value={formData.projectTitle}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Enter project title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  placeholder="Describe your project requirements in detail..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Image (Optional)
                </label>
                <div className="space-y-3">
                  {/* File Upload Field with Enhanced Styling */}
                  <div className={`relative border-2 border-dashed rounded-lg p-4 transition-all duration-200 ${
                    uploadError 
                      ? 'border-red-300 bg-red-50' 
                      : uploadProgress > 0 && uploadProgress < 100
                        ? 'border-purple-300 bg-purple-50'
                        : 'border-gray-300 bg-white hover:border-purple-400 hover:bg-purple-50'
                  }`}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadProgress > 0 && uploadProgress < 100}
                    />
                    <div className="text-center">
                      <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-900">
                          {uploadProgress > 0 && uploadProgress < 100 ? 'Uploading...' : 'Click to upload'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF, WebP up to 5MB
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Upload Error Message */}
                  {uploadError && (
                    <div className="flex items-center bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                      <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-700 text-sm font-medium">{uploadError}</span>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 text-center">
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  )}

                  {/* Image Preview */}
                  {(imagePreview || formData.referenceImage) && (
                    <div className="relative inline-block group">
                      <img
                        src={imagePreview || formData.referenceImage}
                        alt="Reference preview"
                        className="w-32 h-32 object-cover rounded-lg border-2 border-purple-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors shadow-lg"
                        disabled={uploadProgress > 0 && uploadProgress < 100}
                      >
                        ×
                      </button>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-200"></div>
                    </div>
                  )}

                  {/* Upload Success */}
                  {uploadProgress === 100 && (
                    <div className="flex items-center bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-green-700 text-sm font-medium">Image uploaded successfully!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-white transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid() || isSubmitting || (uploadProgress > 0 && uploadProgress < 100)}
              className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceBookingModal;